import { DatePicker as AntdDatePicker } from 'antd';
import React, { ComponentProps, useMemo } from 'react';

import {
  dayjsToMoment,
  momentToDayjs,
  type DatetimeType,
} from '@common/services/datetime';

type AntdProps = ComponentProps<typeof AntdDatePicker>;

// antd's `PickerProps<Moment>` is a union (base | date | time). A plain `Omit`
// over a union collapses to only the keys common to every member, which drops
// date-variant-only props like `showToday`. Distribute the Omit across the union
// so those props survive on the wrapper's public type.
type DistributiveOmit<T, K extends keyof never> = T extends unknown
  ? Omit<T, K>
  : never;

export type DatePickerProps = DistributiveOmit<
  AntdProps,
  'value' | 'defaultValue' | 'defaultPickerValue' | 'onChange' | 'disabledDate'
> & {
  // NOTE: the caller must keep this prop's identity stable across renders --
  // an inline `datetime(x)` re-bridges every render and antd then discards
  // whatever is being typed, memo or not.
  value?: DatetimeType | null;
  defaultValue?: DatetimeType | null;
  defaultPickerValue?: DatetimeType | null;
  onChange?: (date: DatetimeType | null, dateString: string) => void;
  disabledDate?: (current: DatetimeType) => boolean;
};

export function DatePicker({
  value,
  defaultValue,
  defaultPickerValue,
  onChange,
  disabledDate,
  ...rest
}: DatePickerProps) {
  // Bridge once per value: antd's picker resets the text being typed whenever
  // the `value` prop changes identity, so re-deriving a Moment on every render
  // makes a pre-filled picker impossible to type over.
  const momentValue = useMemo(() => dayjsToMoment(value), [value]);
  const momentDefaultValue = useMemo(
    () => dayjsToMoment(defaultValue) ?? undefined,
    [defaultValue]
  );
  const momentDefaultPickerValue = useMemo(
    () => dayjsToMoment(defaultPickerValue) ?? undefined,
    [defaultPickerValue]
  );

  return (
    <AntdDatePicker
      {...rest}
      value={momentValue}
      defaultValue={momentDefaultValue}
      defaultPickerValue={momentDefaultPickerValue}
      onChange={
        onChange
          ? (date, dateString) => onChange(momentToDayjs(date), dateString)
          : undefined
      }
      disabledDate={
        disabledDate
          ? (current) => disabledDate(momentToDayjs(current))
          : undefined
      }
    />
  );
}
